import { Maybe } from "@/domains/common/types";
import { ZIndex } from "@/domains/design/constants";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { electronModule } from "@/modules/electron";
import { forceReloadWindow } from "@/modules/window/reload";
import { useAppStore } from "@/store";
import { DebugOverlayViewKind } from "@/store/debug/AppStoreDebugStore";
import { GatedFeature } from "@/store/feature-gates/types";
import { CustomSyncErrorKind } from "@/store/sync/operations/errors/SyncError";
import { MDSCheckbox } from "@/mds/components/forms/checkbox";
import { getOperationKindToClassMap } from "@/store/sync/operations/map";
import { SyncOperationKind } from "@/store/sync/types";
import { observer } from "mobx-react-lite";
import { clientEnvModule } from "@/modules/client-env";
import {
  RecallSdkState,
  WorkingContextSnapshot,
  WorkingContextStatus,
} from "@/modules/electron/types";
import { DateTime } from "luxon";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonVariant,
  MDSMenuContentItemKind,
  MDSMenuItemSelectionVariant,
  MDSSpacer,
  MDSText,
  MDSButtonSize,
} from "@/mds";
import { SmartViewKind } from "@/domains/smart/constants";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import styled from "@emotion/styled";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { CacheKey } from "@/modules/cache/constants";
import { useQuery } from "@tanstack/react-query";
import { LastUsedAuthMethod } from "@/domains/auth/last-used-auth-method";

const TCC_RESET_APP_NAMES = ["Mem", "Cursor", "WezTerm", "Terminal", "Mem Staging"] as const;

const customErrorKinds: Maybe<CustomSyncErrorKind>[] = [
  undefined,
  "INVALID",
  "PERMISSION_DENIED",
  "TRANSIENT",
  "UNKNOWN",
];

// Type for performance memory API
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

// Performance monitoring sub-component
const PerformanceMetrics = observer(function PerformanceMetrics() {
  const [fps, setFps] = useState(0);
  const [memoryMB, setMemoryMB] = useState(0);
  const [frameTime, setFrameTime] = useState({ max: 0, avg: 0 });
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const frameTimes = useRef<number[]>([]);
  const lastUpdateRef = useRef(performance.now());

  useEffect(() => {
    let animationId: number;

    const measurePerformance = (currentTime: number) => {
      // Calculate FPS
      frameCountRef.current++;
      const deltaTime = currentTime - lastTimeRef.current;

      if (deltaTime >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / deltaTime));
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      // Continuously track frame times in background
      if (frameTimes.current.length > 0) {
        frameTimes.current.push(currentTime);

        // Keep only last 10 frames for lag detection
        if (frameTimes.current.length > 11) {
          // 11 timestamps = 10 frame deltas
          frameTimes.current.shift();
        }
      } else {
        frameTimes.current.push(currentTime);
      }

      // Update memory usage every 5 seconds
      if (currentTime - lastUpdateRef.current >= 5000) {
        const perfWithMemory = performance as PerformanceWithMemory;
        if (perfWithMemory.memory) {
          setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
        }
        lastUpdateRef.current = currentTime;
      }

      animationId = requestAnimationFrame(measurePerformance);
    };

    // Add click listener to analyze recent frame times
    const handleClick = () => {
      if (frameTimes.current.length > 1) {
        // Calculate frame deltas from recent history
        const deltas = [];
        for (let i = 1; i < frameTimes.current.length; i++) {
          deltas.push(frameTimes.current[i] - frameTimes.current[i - 1]);
        }

        if (deltas.length > 0) {
          const maxFrameTime = Math.max(...deltas);
          const avgFrameTime = deltas.reduce((a, b) => a + b, 0) / deltas.length;

          setFrameTime({
            max: Math.round(maxFrameTime),
            avg: Math.round(avgFrameTime),
          });
        }
      }

      // Also update memory immediately on click
      const perfWithMemory = performance as PerformanceWithMemory;
      if (perfWithMemory.memory) {
        setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
      }
    };

    document.addEventListener("click", handleClick, true);
    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return (
    <StyledPerformanceMetrics>
      <span>FPS: {fps.toString().padStart(2, " ")}</span>
      <span>Frame: {frameTime.max}ms</span>
      {<span>Mem: {memoryMB > 0 ? `${memoryMB}MB` : `...`}</span>}
    </StyledPerformanceMetrics>
  );
});

// Debug content sub-component
const ExpandedDebugContent = observer(function ExpandedDebugContent() {
  const { store } = useAppStore();
  const { buildInfo } = clientEnvModule.buildInfo();

  const operationKinds: Maybe<SyncOperationKind>[] = [
    undefined,
    ...(Object.keys(getOperationKindToClassMap()) as SyncOperationKind[]),
  ];

  const [actionQueueExpanded, setActionQueueExpanded] = useState(true);

  return (
    <StyledDebugContent>
      <StyledCheckboxContainer>
        <MDSCheckbox
          isSelected={store.debug.forceOnboardingMode}
          onChange={() => store.debug.toggleForceOnboardingMode()}
        >
          <MDSText>Force Chat Onboarding Mode</MDSText>
          <MDSText>This will force render special onboarding empty state for chat</MDSText>
        </MDSCheckbox>
      </StyledCheckboxContainer>
      <StyledMockApiErrorRow>
        <MDSSpacer />
        <MDSActionMenuDropdown
          placement="bottom right"
          contentList={customErrorKinds.map(kind => ({
            id: kind ?? "DISABLED",
            kind: MDSMenuContentItemKind.Item,
            label: kind ?? "DISABLED",
            isSelected: store.debug.operationsSubmitApiMockErrorKind === kind,
            selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
            onClick: () => store.debug.setOperationsSubmitApiMockErrorKind(kind),
          }))}
        >
          <MDSButton>{store.debug.operationsSubmitApiMockErrorKind ?? "DISABLED"}</MDSButton>
        </MDSActionMenuDropdown>
        {"|"}
        <MDSActionMenuDropdown
          contentList={operationKinds.map(kind => ({
            id: kind ?? "all",
            kind: MDSMenuContentItemKind.Item,
            label: kind ?? "all",
            isSelected: store.debug.operationsSubmitApiMockErrorOperationKind === kind,
            selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
            onClick: () => store.debug.setOperationsSubmitApiMockErrorOperationKind(kind),
          }))}
        >
          <MDSButton>{store.debug.operationsSubmitApiMockErrorOperationKind ?? "ALL"}</MDSButton>
        </MDSActionMenuDropdown>
      </StyledMockApiErrorRow>
      <StyledDebugInfo onClick={e => e.altKey && setActionQueueExpanded(!actionQueueExpanded)}>
        {!actionQueueExpanded && "<Alt + click to expand action queue info>"}
        {actionQueueExpanded && JSON.stringify(store.sync.actionQueue.friendlyDebugInfo, null, 4)}
      </StyledDebugInfo>
      <br />
      <StyledDebugInfo>
        <span>Commit: {buildInfo.gitCommitSha}</span>
        <br />
        <span>
          Build at:{" "}
          {buildInfo.buildTimestamp.toLocal().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </span>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

// Database view content
const ExpandedDatabaseContent = observer(function ExpandedDatabaseContent() {
  const { store } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSizeBytes, setTotalSizeBytes] = useState<number | null>(null);
  const [rowCounts, setRowCounts] = useState<Array<{ table: string; rowCount: number }>>([]);

  const isDbInitialized = store.db.isInitialized();

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${value} ${sizes[i]}`;
  };

  const refresh = async () => {
    if (!isDbInitialized) return;
    setLoading(true);
    setError(null);
    try {
      const sizeBytes = await store.db.stats.getTotalSizeBytes();
      setTotalSizeBytes(sizeBytes);

      const counts = await store.db.stats.getRowCountsByTable();
      setRowCounts(counts);
    } catch (unknownErr) {
      const err = unknownErr as Error;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDbInitialized]);

  if (!isDbInitialized) {
    return <StyledDebugContent>Heads-up DB not initialized.</StyledDebugContent>;
  }

  return (
    <StyledDebugContent>
      <StyledButtonGroup>
        <MDSButton variant={MDSButtonVariant.Transparent} onClick={refresh} isLoading={loading}>
          Refresh
        </MDSButton>
      </StyledButtonGroup>

      {error && <StyledErrorText>Error: {error}</StyledErrorText>}

      <StyledDebugInfo>
        <StyledStorageInfo>
          <strong>Total storage:</strong>{" "}
          {totalSizeBytes !== null ? formatBytes(totalSizeBytes) : "..."}
        </StyledStorageInfo>
        <div>
          <strong>Row counts (public):</strong>
        </div>
        <div>
          {rowCounts.length === 0 && "..."}
          {rowCounts.length > 0 && (
            <StyledUnorderedList>
              {rowCounts.map(({ table, rowCount }) => (
                <StyledListItem key={table}>
                  <span>{table}</span>
                  <span>{rowCount.toLocaleString()}</span>
                </StyledListItem>
              ))}
            </StyledUnorderedList>
          )}
        </div>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Debug content component for Heads Up sidebar state inspection
 */
const ExpandedHeadsUpContent = observer(function ExpandedHeadsUpContent() {
  const { store } = useAppStore();
  const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId ?? null;
  const liveNoteId = store.routing.noteIdParam ?? null;

  const contextId = resolveSmartContextId({
    spaceAccountId,
    activeNoteId: liveNoteId ?? null,
    smartViewKind: SmartViewKind.HeadsUp,
  });

  const { context } = store.db.smartContexts.useById({
    contextId,
  });

  const smartContextId = context?.id ?? contextId;

  const { view } = store.db.smartViews.useLatestByContextId({
    smartContextId,
  });

  const { cards } = store.db.smartCards.useCardsBySmartViewId({
    smartViewId: view?.id ?? null,
  });

  const summary = {
    noteId: liveNoteId,
    contextId: smartContextId,
    contextPayload: context?.payload_json ?? null,
    viewId: view?.id ?? null,
    viewRefreshedAt: view?.refreshed_at ?? null,
    cardCount: cards.length,
  };

  return (
    <StyledDebugContent>
      <StyledDebugInfo>{JSON.stringify(summary, null, 2)}</StyledDebugInfo>
      <StyledDebugInfo>{JSON.stringify(cards, null, 2)}</StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Shows the number of observables in memory at any given time
 */
const ExpandedMemoryContent = observer(function ExpandedMemoryContent() {
  const { store } = useAppStore();
  return (
    <StyledDebugContent>
      <StyledDebugInfo>
        <StyledSectionTitle># Observables in Memory (Current / Loaded / Freed)</StyledSectionTitle>
        {Object.entries(store.sync.stores)
          .filter(([, storeInstance]) => storeInstance !== undefined)
          .map(([kind, storeInstance]) => {
            let freedCount = 0;
            let currentCount = 0;
            for (const weakRefOrUndefined of storeInstance!.pool.values()) {
              if (weakRefOrUndefined) {
                const model = weakRefOrUndefined.deref();
                if (model === undefined) {
                  freedCount++;
                } else {
                  currentCount++;
                }
              }
            }
            return (
              <StyledMemoryRow key={kind}>
                <span>{kind.replace(/_/g, " ")}</span>
                <span>
                  C: {currentCount} | L: {storeInstance!.pool.size} | F: {freedCount}
                </span>
              </StyledMemoryRow>
            );
          })}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Shows the number of meeting detection debug events
 */
const ExpandedElectronContent = observer(function ExpandedElectronContent() {
  const { store } = useAppStore();
  const [isTestingShutdown, setIsTestingShutdown] = useState(false);
  const [isResettingTcc, setIsResettingTcc] = useState(false);
  const [tccAppName, setTccAppName] = useState("Mem");
  const isRecallReady = store.electron.recallSdkState === RecallSdkState.Running;

  const handleTestUnexpectedShutdown = useCallback(async () => {
    if (isTestingShutdown || !clientEnvModule.isDesktop() || !isRecallReady) return;

    setIsTestingShutdown(true);
    try {
      await electronModule.recall.testUnexpectedShutdown?.();
      toastModule.triggerToast({ content: "Triggered Recall unexpected shutdown test." });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Error testing unexpected shutdown",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      toastModule.triggerToast({ content: "Recall shutdown test failed. Check logs." });
    } finally {
      setIsTestingShutdown(false);
    }
  }, [isTestingShutdown, isRecallReady]);

  return (
    <StyledDebugContent>
      <StyledSectionTitle>Electron</StyledSectionTitle>
      <StyledDebugInfo>
        <div>
          recallSdkState: <b>{store.electron.recallSdkState}</b>
        </div>
        {store.electron.recallSdkStateErrorMessage ? (
          <div>error: {store.electron.recallSdkStateErrorMessage}</div>
        ) : null}
        {clientEnvModule.isDesktop() && (
          <MDSButton
            variant={MDSButtonVariant.Default}
            size={MDSButtonSize.Small}
            onClick={handleTestUnexpectedShutdown}
            isLoading={isTestingShutdown}
          >
            Test unexpected shutdown
          </MDSButton>
        )}
      </StyledDebugInfo>
      <StyledSectionTitle># Meeting Detection Debug Events</StyledSectionTitle>
      <StyledSectionTitle>Electron info</StyledSectionTitle>
      <StyledDebugInfo>{JSON.stringify(store.electron.electronAppInfo, null, 2)}</StyledDebugInfo>

      <StyledSectionTitle>Meeting Detection Debug Events</StyledSectionTitle>
      <StyledDebugInfo>
        {store.electron.meetingDetectionDebugEvents.map(event => {
          const { timestamp, data, kind } = event;
          return (
            <Fragment key={event.timestamp.toJSDate().getTime()}>
              <b>
                {timestamp.toFormat("HH:mm:ss.SSS")} {kind}
              </b>
              {Object.entries(data).map(([key, value]) =>
                typeof value === "object" ? null : (
                  <>
                    <br />
                    {key}: {JSON.stringify(value, null, 2)}
                  </>
                )
              )}
              <br />
            </Fragment>
          );
        })}
      </StyledDebugInfo>
      <StyledSectionTitle>Reset TCC &amp; Clear Cache</StyledSectionTitle>
      <StyledDebugInfo>
        Resets macOS TCC permissions for the selected app and clears the device cache. Select the
        app you use to launch Electron in dev, or &quot;Mem&quot; for the production build. The app
        will restart automatically.
        <br />
        <br />
        <MDSActionMenuDropdown
          placement="bottom right"
          contentList={TCC_RESET_APP_NAMES.map(name => ({
            id: name,
            kind: MDSMenuContentItemKind.Item,
            label: name,
            isSelected: tccAppName === name,
            selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
            onClick: () => setTccAppName(name),
          }))}
        >
          <MDSButton size={MDSButtonSize.Small} variant={MDSButtonVariant.Default}>
            {tccAppName}
          </MDSButton>
        </MDSActionMenuDropdown>{" "}
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          isLoading={isResettingTcc}
          onClick={async () => {
            setIsResettingTcc(true);
            try {
              await electronModule.resetTccAndClearCache({ appName: tccAppName });
              toastModule.triggerToast({ content: "Reset complete. Restarting..." });
              await electronModule.restartApp();
            } catch (unknownError) {
              const error = unknownError as Error;
              logger.error({
                message: "Reset TCC & clear cache failed",
                info: { error: objectModule.safeErrorAsJson(error) },
              });
              toastModule.triggerToast({ content: "Reset failed. Check logs." });
            } finally {
              setIsResettingTcc(false);
            }
          }}
        >
          Reset TCC &amp; Clear Cache
        </MDSButton>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

type WorkingContextDebugPayload = {
  status: WorkingContextStatus;
  snapshot: WorkingContextSnapshot | null;
};

const ExpandedWorkingContextContent = observer(function ExpandedWorkingContextContent() {
  const [subscriptionId, setSubscriptionId] = useState("debug");
  const [subscriptionQuery, setSubscriptionQuery] = useState("");
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const isDesktop = clientEnvModule.isDesktop();

  const { data, error, isFetching, refetch } = useQuery<WorkingContextDebugPayload>({
    enabled: isDesktop,
    queryKey: ["working-context-debug"],
    queryFn: async () => {
      const [status, snapshot] = await Promise.all([
        electronModule.workingContext.getStatus(),
        electronModule.workingContext.getSnapshot(),
      ]);

      return { status, snapshot };
    },
    refetchInterval: 5_000,
  });

  const handleAddSubscription = useCallback(async () => {
    if (!isDesktop || isSavingSubscription) return;

    setIsSavingSubscription(true);

    try {
      await electronModule.workingContext.addSubscription({
        subscriptionId,
        query: subscriptionQuery,
      });
      await refetch();
    } finally {
      setIsSavingSubscription(false);
    }
  }, [subscriptionId, subscriptionQuery, isDesktop, isSavingSubscription, refetch]);

  const handleRemoveSubscription = useCallback(async () => {
    if (!isDesktop || isSavingSubscription) return;

    setIsSavingSubscription(true);

    try {
      await electronModule.workingContext.removeSubscription({ subscriptionId });
      setSubscriptionQuery("");
      await refetch();
    } finally {
      setIsSavingSubscription(false);
    }
  }, [subscriptionId, isDesktop, isSavingSubscription, refetch]);

  if (!isDesktop) {
    return (
      <StyledDebugContent>Working context is only available in the desktop app.</StyledDebugContent>
    );
  }

  const debugPayload = {
    status: data?.status ?? null,
    activeSubscriptions: data?.snapshot?.activeSubscriptions ?? [],
    focus: data?.snapshot?.focus ?? null,
    recentMatches: data?.snapshot?.recentMatches ?? [],
    recentObservations: data?.snapshot?.recentObservations ?? [],
  };

  return (
    <StyledDebugContent>
      <StyledSectionTitle>Subscription ID</StyledSectionTitle>
      <StyledDebugInput
        value={subscriptionId}
        onChange={event => setSubscriptionId(event.currentTarget.value)}
        placeholder="subscription id"
      />
      <StyledSectionTitle>Subscription Query</StyledSectionTitle>
      <StyledDebugTextArea
        value={subscriptionQuery}
        onChange={event => setSubscriptionQuery(event.currentTarget.value)}
        placeholder="What should working context watch for?"
      />
      <StyledButtonGroup>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={handleAddSubscription}
          isLoading={isSavingSubscription}
        >
          Add / update
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Transparent}
          onClick={handleRemoveSubscription}
          isLoading={isSavingSubscription}
        >
          Remove
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Transparent}
          onClick={() => void refetch()}
          isLoading={isFetching}
        >
          Refresh
        </MDSButton>
      </StyledButtonGroup>
      {error ? <StyledErrorText>{String(error)}</StyledErrorText> : null}
      <StyledSectionTitle>Snapshot</StyledSectionTitle>
      <StyledDebugInfo>{JSON.stringify(debugPayload, null, 2)}</StyledDebugInfo>
    </StyledDebugContent>
  );
});

const ExpandedCacheContent = observer(function ExpandedCacheContent() {
  const { store } = useAppStore();

  const perDeviceOnboardingInfoResult = useQuery({
    queryKey: [CacheKey.PerDeviceOnboardingInfo],
    queryFn: () => store.accountCache.get(CacheKey.PerDeviceOnboardingInfo),
  });

  return (
    <StyledDebugContent>
      <StyledSectionTitle>LastUsedAuthMethod</StyledSectionTitle>
      <StyledDebugInfo>
        {JSON.stringify(store.auth.lastUsedAuthMethodRecord, null, 2)}
        <br />
        <br />
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            void store.auth.clearLastUsedAuthMethod();
          }}
        >
          Clear
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            void store.auth.setLastUsedAuthMethod(LastUsedAuthMethod.Google);
          }}
        >
          Set Google
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            void store.auth.setLastUsedAuthMethod(LastUsedAuthMethod.Apple);
          }}
        >
          Set Apple
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            void store.auth.setLastUsedAuthMethod(LastUsedAuthMethod.EmailPassword);
          }}
        >
          Set Email
        </MDSButton>
      </StyledDebugInfo>
      <StyledSectionTitle>PerDeviceOnboardingInfo</StyledSectionTitle>
      <div>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => perDeviceOnboardingInfoResult.refetch()}
        >
          Refresh
        </MDSButton>
      </div>
      <StyledDebugInfo>
        {JSON.stringify(perDeviceOnboardingInfoResult.data, null, 2)}
        <br />
        <br />
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.del(CacheKey.PerDeviceOnboardingInfo);
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Clear
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, {
              hasDismissedMeetingOnboardingSystemPermissions: false,
              hasDismissedVoiceOnboardingSystemPermissions: false,
              hasCompletedOrSkippedNativeAppDownload: false,
            });
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Set isDismissed to False
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, {
              hasDismissedMeetingOnboardingSystemPermissions: true,
              hasDismissedVoiceOnboardingSystemPermissions: true,
              hasCompletedOrSkippedNativeAppDownload: true,
            });
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Set isDismissed to True
        </MDSButton>
        <br />
        You&apos;ll need to quit-and-reopen for Mem to pick up MacOS permission changes.
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

// Onboarding debug panel
const ExpandedOnboardingContent = observer(function ExpandedOnboardingContent() {
  const { store } = useAppStore();
  const onboarding = store.onboarding;
  const ctx = onboarding.context;
  const allSteps = onboarding.allSteps;
  const completedIds = new Set(onboarding.completedSteps.map(s => s.id));
  const currentId = onboarding.currentStepId;
  const [isResettingAll, setIsResettingAll] = useState(false);
  const [tccAppName, setTccAppName] = useState("Mem");

  const statusLabel = onboarding.isActive ? "Active" : "Done";

  return (
    <StyledDebugContent>
      <StyledSectionTitle>Onboarding: {statusLabel}</StyledSectionTitle>
      <StyledSectionTitle>Steps</StyledSectionTitle>
      <StyledOnboardingStepList>
        {allSteps.map(step => {
          const isApplicable = step.applicableWhen(ctx);
          const isCompleted = isApplicable && completedIds.has(step.id);
          const isCurrent = step.id === currentId;

          // Resolve active substep for the current step
          const activeSubstepId = isCurrent
            ? (step.substeps.find(s => s.activeWhen(ctx))?.id ?? step.substeps[0]?.id)
            : undefined;

          let icon: string;
          let color: string;
          if (!isApplicable) {
            icon = "\u2212";
            color = "#666";
          } else if (isCompleted) {
            icon = "\u2713";
            color = "#4caf50";
          } else if (isCurrent) {
            icon = "\u25b6";
            color = "#2196f3";
          } else {
            icon = "\u00b7";
            color = "#999";
          }

          return (
            <div key={step.id}>
              <StyledOnboardingStepRow style={{ color }}>
                <StyledOnboardingIcon style={{ color }}>{icon}</StyledOnboardingIcon>
                <StyledOnboardingStepContent>
                  <span style={!isApplicable ? { textDecoration: "line-through" } : undefined}>
                    {step.id}
                  </span>
                  <StyledOnboardingDebugLabels>
                    {step.debugApplicableLabel && (
                      <StyledOnboardingDebugLabel>
                        applicable: {step.debugApplicableLabel(ctx)}
                      </StyledOnboardingDebugLabel>
                    )}
                    {isApplicable && step.debugCompletedLabel && (
                      <StyledOnboardingDebugLabel>
                        completed: {step.debugCompletedLabel(ctx)}
                      </StyledOnboardingDebugLabel>
                    )}
                  </StyledOnboardingDebugLabels>
                </StyledOnboardingStepContent>
                {isApplicable && step.debugReset && (
                  <StyledOnboardingResetButton
                    onClick={e => {
                      e.stopPropagation();
                      step.debugReset?.(store);
                    }}
                  >
                    reset
                  </StyledOnboardingResetButton>
                )}
              </StyledOnboardingStepRow>
              {isApplicable &&
                step.substeps.length > 1 &&
                step.substeps.map((sub, subIdx) => {
                  const isActiveSub = isCurrent && sub.id === activeSubstepId;
                  const activeSubIdx = isCurrent
                    ? step.substeps.findIndex(s => s.id === activeSubstepId)
                    : -1;
                  const isSubCompleted = isCompleted || (isCurrent && activeSubIdx > subIdx);
                  const subColor = isSubCompleted ? "#4caf50" : isActiveSub ? "#2196f3" : "#666";
                  const subIcon = isSubCompleted ? "\u2713" : isActiveSub ? "\u25b6" : "\u00b7";
                  return (
                    <StyledOnboardingSubstepRow key={sub.id} style={{ color: subColor }}>
                      <StyledOnboardingIcon style={{ color: subColor }}>
                        {subIcon}
                      </StyledOnboardingIcon>
                      <StyledOnboardingStepContent>
                        <span>{sub.id}</span>
                        {sub.debugActiveLabel && (
                          <StyledOnboardingDebugLabels>
                            <StyledOnboardingDebugLabel>
                              {sub.debugActiveLabel(ctx)}
                            </StyledOnboardingDebugLabel>
                          </StyledOnboardingDebugLabels>
                        )}
                      </StyledOnboardingStepContent>
                    </StyledOnboardingSubstepRow>
                  );
                })}
            </div>
          );
        })}
      </StyledOnboardingStepList>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
        {clientEnvModule.isDesktop() && (
          <MDSActionMenuDropdown
            placement="bottom right"
            contentList={TCC_RESET_APP_NAMES.map(name => ({
              id: name,
              kind: MDSMenuContentItemKind.Item,
              label: name,
              isSelected: tccAppName === name,
              selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
              onClick: () => setTccAppName(name),
            }))}
          >
            <MDSButton size={MDSButtonSize.Small} variant={MDSButtonVariant.Default}>
              Reset TCC for app: {tccAppName}
            </MDSButton>
          </MDSActionMenuDropdown>
        )}
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.DangerFilled}
          isLoading={isResettingAll}
          onClick={async () => {
            setIsResettingAll(true);
            try {
              await onboarding.debugResetAll();
              if (clientEnvModule.isDesktop()) {
                await electronModule.resetTccAndClearCache({ appName: tccAppName });
              }
              toastModule.triggerToast({ content: "Full reset complete. Restarting..." });
              if (clientEnvModule.isDesktop()) {
                await electronModule.restartApp();
              } else {
                await forceReloadWindow();
              }
            } catch (unknownError) {
              const error = unknownError as Error;
              logger.error({
                message: "Reset All failed",
                info: { error: objectModule.safeErrorAsJson(error) },
              });
              toastModule.triggerToast({ content: "Reset All failed. Check logs." });
            } finally {
              setIsResettingAll(false);
            }
          }}
        >
          Reset All
        </MDSButton>
      </div>
    </StyledDebugContent>
  );
});

const ExpandedSearchContent = observer(function ExpandedSearchContent() {
  const { store } = useAppStore();

  return (
    <StyledDebugContent>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onClick={async () => {
          const response = store.debug.latestSmartSearchResponse;
          if (!response) {
            toastModule.triggerToast({ content: "No smart search response available." });
            return;
          }
          await copyTextToClipboard({ text: JSON.stringify(response, null, 2) });
          toastModule.triggerToast({ content: "Copied smart search response to clipboard." });
        }}
      >
        Copy Smart Search Response
      </MDSButton>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onClick={async () => {
          const response = store.debug.latestFastSearchResponse;
          if (!response) {
            toastModule.triggerToast({ content: "No fast search response available." });
            return;
          }
          await copyTextToClipboard({ text: JSON.stringify(response, null, 2) });
          toastModule.triggerToast({ content: "Copied fast search response to clipboard." });
        }}
      >
        Copy Fast Search Response
      </MDSButton>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onClick={async () => {
          const query = store.debug.latestSearchQuery;
          if (!query) {
            toastModule.triggerToast({ content: "No search query available." });
            return;
          }
          await copyTextToClipboard({ text: decodeURIComponent(query) });
          toastModule.triggerToast({ content: "Copied search query to clipboard." });
        }}
      >
        Copy Query
      </MDSButton>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onClick={async () => {
          const smart = store.debug.latestSmartSearchResponse;
          const fast = store.debug.latestFastSearchResponse;
          const query = store.debug.latestSearchQuery;
          if (!smart && !fast && !query) {
            toastModule.triggerToast({ content: "No search data available." });
            return;
          }
          await copyTextToClipboard({
            text: JSON.stringify(
              {
                query: query ? decodeURIComponent(query) : null,
                smart_search: smart,
                fast_search: fast,
              },
              null,
              2
            ),
          });
          toastModule.triggerToast({ content: "Copied all search data to clipboard." });
        }}
      >
        Copy All
      </MDSButton>
    </StyledDebugContent>
  );
});

const ExpandedMonetizationContent = observer(function ExpandedMonetizationContent() {
  const { store } = useAppStore();

  const gates = [
    { label: "Notes", id: GatedFeature.NoteCreate },
    { label: "Chat Messages", id: GatedFeature.ChatMessageSend },
    { label: "PDF Pages", id: GatedFeature.ContentUnderstandingPdfPage },
  ] as const;

  const tier = store.subscriptions.personalSubscription?.tier ?? "unknown";

  return (
    <StyledDebugContent>
      <StyledSectionTitle>Plan</StyledSectionTitle>
      <StyledDebugInfo>{tier}</StyledDebugInfo>

      <StyledSectionTitle>Feature Gates</StyledSectionTitle>
      {gates.map(({ label, id }) => {
        const result = store.featureGates.checkGate(id);
        return (
          <Fragment key={id}>
            <StyledSectionTitle>{label}</StyledSectionTitle>
            <StyledDebugInfo>
              {JSON.stringify(
                {
                  feature: id,
                  allowed: result.allowed,
                  used: result.used,
                  limit: result.limit === Infinity ? "Infinity" : result.limit,
                  warnAt: result.warnAt,
                },
                null,
                2
              )}
            </StyledDebugInfo>
          </Fragment>
        );
      })}

      <StyledSectionTitle>Banner State</StyledSectionTitle>
      <StyledDebugInfo>
        {JSON.stringify(
          {
            upsellBannerVisible: store.featureGates.upsellBannerVisible,
            upsellBannerExceeded: store.featureGates.upsellBannerExceeded,
          },
          null,
          2
        )}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Neutralize modal scrims when the debug panel is toggled open.
 *
 * react-aria's `ariaHideOutside` sets the HTML `inert` attribute on sibling
 * elements when a modal is open, which blocks all interaction regardless of
 * z-index or CSS. This hook:
 * 1. Removes `position:fixed` from full-screen scrim overlays so they collapse
 * 2. Uses a MutationObserver to strip `inert` as fast as react-aria applies it
 */
const neutralizeScrimElements = () => {
  document.querySelectorAll("[inert]").forEach(el => {
    el.removeAttribute("inert");
  });

  // Target only direct children of body (where react-aria portals live)
  // plus the interstitial overlay inside the app root
  document
    .querySelectorAll<HTMLElement>("body > div, [data-overlay-container] > div")
    .forEach(el => {
      const style = window.getComputedStyle(el);
      if (style.position === "fixed" && (style.zIndex === "400" || style.zIndex === "50")) {
        el.dataset.debugScrimNeutralized = "true";
        el.style.position = "static";
      }
    });
};

const restoreScrimElements = () => {
  document.querySelectorAll<HTMLElement>("[data-debug-scrim-neutralized]").forEach(el => {
    el.style.position = "";
    delete el.dataset.debugScrimNeutralized;
  });
};

const useNeutralizeModalScrimsWhenDebugPanelOpen = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) return;

    neutralizeScrimElements();

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "inert") {
          const el = mutation.target as HTMLElement;
          if (el.hasAttribute("inert")) {
            el.removeAttribute("inert");
          }
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["inert"],
      subtree: true,
    });

    return () => {
      observer.disconnect();
      restoreScrimElements();
    };
  }, [isOpen]);
};

/**
 * This is a debugging overlay which displays various pieces of info about local application state.
 * Most notably, it displays the current sync action queue, and allows for copying the queue's debug info to the clipboard.
 */
export const DebugInfoOverlay = observer(function DebugInfoOverlay() {
  const { store } = useAppStore();

  const isOpen = store.debug.debugModeEnabled && store.debug.debugModeToolbarExpanded;
  useNeutralizeModalScrimsWhenDebugPanelOpen(isOpen);

  if (!store.debug.debugModeEnabled) {
    return null;
  }

  const toggleOpen = () => store.debug.toggleDebugModeToolbar();

  return (
    <>
      {/* Overlay content when expanded */}
      {isOpen && (
        <StyledOverlayContent>
          {store.debug.debugOverlayView === DebugOverlayViewKind.Standard && (
            <ExpandedDebugContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Database && (
            <ExpandedDatabaseContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Account && (
            <ExpandedFeatureFlagsContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.FeatureFlags && (
            <ExpandedFeatureFlagsManagementContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.HeadsUp && (
            <ExpandedHeadsUpContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Memory && (
            <ExpandedMemoryContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Electron && (
            <ExpandedElectronContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.WorkingContext && (
            <ExpandedWorkingContextContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Cache && <ExpandedCacheContent />}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Onboarding && (
            <ExpandedOnboardingContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Search && (
            <ExpandedSearchContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Monetization && (
            <ExpandedMonetizationContent />
          )}
        </StyledOverlayContent>
      )}

      {/* Navigation bar - visible when expanded */}
      {isOpen && (
        <StyledNavBar>
          <StyledNavRow>
            <StyledNavLeft>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Standard)}
              >
                Standard
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Database)}
              >
                DB
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.HeadsUp)}
              >
                Heads Up
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Account)}
              >
                Account
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.FeatureFlags)}
              >
                Flags
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Memory)}
              >
                Memory
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Electron)}
              >
                Electron
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.WorkingContext)}
              >
                Context
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Cache)}
              >
                Cache
              </MDSButton>
            </StyledNavLeft>
          </StyledNavRow>
          <StyledNavRow>
            <StyledNavRight>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Onboarding)}
              >
                Onboarding
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Search)}
              >
                Search
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Monetization)}
              >
                Monetization
              </MDSButton>
              <MDSButton
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.sync.actionQueue.saveDebugInfoToFile()}
              >
                Save to file
              </MDSButton>
              <MDSButton
                variant={MDSButtonVariant.Transparent}
                onClick={async () => {
                  const fullDebugInfo = store.sync.actionQueue.fullDebugInfo;
                  await copyTextToClipboard({
                    text: JSON.stringify(fullDebugInfo, null, 4),
                  });
                  toastModule.triggerToast({ content: "Copied queue debug info to clipboard." });
                }}
              >
                Copy Info
              </MDSButton>
            </StyledNavRight>
          </StyledNavRow>
        </StyledNavBar>
      )}

      {/* Bottom bar - always visible */}
      <StyledBottomBar isOpen={isOpen}>
        <StyledHeader>
          {!isOpen && (
            <>
              <PerformanceMetrics />
              <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                Expand
              </MinimizeButton>
            </>
          )}
          {isOpen && (
            <>
              <PerformanceMetrics />
              <StyledHeaderButtonGroup>
                <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                  Minimize
                </MinimizeButton>
              </StyledHeaderButtonGroup>
            </>
          )}
        </StyledHeader>
      </StyledBottomBar>
    </>
  );
});

const MinimizeButton = styled(MDSButton)`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  padding: 2px 4px;
`;

const StyledBottomBar = styled.div<{ isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 0;
  padding: 4px 8px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: ${({ isOpen }) => (isOpen ? "600px" : "auto")};
  height: 34px;
  z-index: ${ZIndex.DebugOverlay};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0.8)};
  transition: all 0.2s ease;
`;

const StyledOverlayContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 120px; /* Position above nav bar (80px for two rows) and bottom bar (34px) */
  padding: 16px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: 600px;
  max-height: calc(100vh - 140px);
  overflow: auto;
  z-index: ${ZIndex.DebugOverlay - 1};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledNavBar = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 42px; /* Just above the bottom bar */
  padding: 4px 8px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: 600px;
  z-index: ${ZIndex.DebugOverlay};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledNavRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledNavLeft = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledNavRight = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  gap: 16px;
`;

const StyledHeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledPerformanceMetrics = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  font-family: monospace;
  color: ${({ theme }) => theme.colors.text.primary};
  align-items: center;

  & > span {
    white-space: nowrap;

    &:nth-of-type(1) {
      width: 60px; /* FPS: XX */
    }

    &:nth-of-type(2) {
      width: 80px; /* Frame: XXms */
    }

    &:nth-of-type(3) {
      width: 80px; /* Mem: XXXmb */
    }
  }
`;

const StyledDebugContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledMockApiErrorRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledDebugInfo = styled.pre`
  font-size: 12px;
  overflow: auto;
  max-height: 300px;
  background-color: ${({ theme }) => theme.colors.surface.modal};
  padding: 12px;
  border-radius: 4px;
  font-family: monospace;
`;

const StyledDebugInput = styled.input`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: monospace;
  font-size: 12px;
  margin-bottom: 8px;
  padding: 8px;
`;

const StyledDebugTextArea = styled.textarea`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: monospace;
  font-size: 12px;
  min-height: 64px;
  padding: 8px;
  resize: vertical;
`;

const StyledErrorText = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const StyledFeatureFlagItem = styled.li<{ isOn: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
  background-color: ${({ theme, isOn }) =>
    isOn ? theme.colors.background.banner.emphasis : theme.colors.background.banner.default};
`;

const StyledFeatureFlagStatus = styled.span<{ isOn: boolean }>`
  font-weight: 600;
  color: ${({ theme, isOn }) => (isOn ? theme.colors.text.primary : theme.colors.text.secondary)};
  text-transform: uppercase;
  font-size: 11px;
`;

const StyledCheckboxContainer = styled.div`
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.background.banner.default};
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledStorageInfo = styled.div`
  margin-bottom: 8px;
`;

const StyledUnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledListItem = styled.li`
  display: flex;
  justify-content: space-between;
`;

const StyledMemoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  &:last-child {
    border-bottom: none;
  }
`;

const StyledSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
`;

const StyledOnboardingStepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: monospace;
  font-size: 12px;
`;

const StyledOnboardingStepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
`;

const StyledOnboardingStepContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const StyledOnboardingDebugLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;
`;

const StyledOnboardingDebugLabel = styled.span`
  font-size: 10px;
  opacity: 0.7;
  font-style: italic;
`;

const StyledOnboardingResetButton = styled.button`
  font-size: 10px;
  font-family: monospace;
  padding: 1px 6px;
  border: 1px solid #666;
  border-radius: 3px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 1px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StyledOnboardingSubstepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 2px 0 2px 20px;
`;

const StyledOnboardingIcon = styled.span`
  width: 14px;
  text-align: center;
  font-weight: 600;
`;

// Feature flags management view content (read-only)
const ExpandedFeatureFlagsManagementContent = observer(
  function ExpandedFeatureFlagsManagementContent() {
    const { store } = useAppStore();
    const flags = store.spaceAccountFeatureFlags.featureFlags;
    const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

    return (
      <StyledDebugContent>
        <StyledDebugInfo>
          {entries.length === 0 ? (
            <div>No feature flags loaded.</div>
          ) : (
            <StyledUnorderedList>
              {entries.map(([flagKey, value]) => {
                const isOn = Boolean(value);

                return (
                  <StyledFeatureFlagItem key={flagKey} isOn={isOn}>
                    <span>{flagKey}</span>
                    <StyledFeatureFlagStatus isOn={isOn}>
                      {isOn ? "ON" : "OFF"}
                    </StyledFeatureFlagStatus>
                  </StyledFeatureFlagItem>
                );
              })}
            </StyledUnorderedList>
          )}
        </StyledDebugInfo>
      </StyledDebugContent>
    );
  }
);

// Feature flags view content (Account tab - read-only)
const ExpandedFeatureFlagsContent = observer(function ExpandedFeatureFlagsContent() {
  const { store } = useAppStore();
  const flags = store.spaceAccountFeatureFlags.featureFlags;
  const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <StyledDebugContent>
      <StyledDebugInfo>
        {entries.length === 0 ? (
          <div>No feature flags loaded.</div>
        ) : (
          <StyledUnorderedList>
            {entries.map(([key, value]) => {
              const isOn = Boolean(value);
              return (
                <StyledFeatureFlagItem key={key} isOn={isOn}>
                  <span>{key}</span>
                  <StyledFeatureFlagStatus isOn={isOn}>
                    {isOn ? "ON" : "OFF"}
                  </StyledFeatureFlagStatus>
                </StyledFeatureFlagItem>
              );
            })}
          </StyledUnorderedList>
        )}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});
